import {
  useMemo,
  useState,
  type CSSProperties,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from 'react';
import { Button } from './Button';

// EntityFormDrawer — shared "+ Create X" form pattern (mock-crud.A1).
//
// Why this exists: every "+ Add study / chapter / meeting / task" affordance
// across the mock used to be either stubbed (no-op button) or one-off (each
// surface inventing its own form). This primitive collapses the four new
// Create flows down to a single shape: `{ title, fields, onSubmit }`.
//
// In-memory only by design (mock-crud arc decision 2026-05-15): on submit,
// the consumer pushes the new entity into its Context provider's array.
// Reloads reset session state (approvals, acks, cursor); persona persists via localStorage.
//
// Renders inside the existing drawer host (DrawerProvider's `'entity-form'`
// kind) — the consumer dispatches via `open('entity-form', { data })`
// rather than rendering this component directly. Keeps overlay focus +
// keyboard behaviour consistent with the rest of the drawer system.
//
// Field types covered (2026-05-16): text, textarea, date, select, multiselect,
// checkbox. Adequate for the 6 mock-crud Create flows. Extend the union as
// new flows demand.

export type EntityFormFieldDef =
  | { key: string; label: string; type: 'text'; placeholder?: string; required?: boolean; hint?: string }
  | { key: string; label: string; type: 'textarea'; placeholder?: string; required?: boolean; rows?: number; hint?: string }
  | { key: string; label: string; type: 'date'; required?: boolean; hint?: string }
  | { key: string; label: string; type: 'select'; options: Array<{ value: string; label: string }>; required?: boolean; hint?: string }
  | { key: string; label: string; type: 'multiselect'; options: Array<{ value: string; label: string }>; hint?: string }
  | { key: string; label: string; type: 'checkbox'; hint?: string };

export type EntityFormValues = Record<
  string,
  string | string[] | boolean | undefined
>;

export type EntityFormDrawerProps = {
  /** Optional intro copy at the top of the form body — context for the form.
   *  Title + kicker are rendered by the drawer chrome from the caller's
   *  `open('entity-form', { title, kicker, data: props })` invocation, so this
   *  body skips inline duplication. */
  intro?: ReactNode;
  /** Field definitions in render order. */
  fields: EntityFormFieldDef[];
  /** Submit-button label. Defaults to "Create". */
  submitLabel?: string;
  /** Called with the form values on submit (consumer pushes to its Context). */
  onSubmit: (values: EntityFormValues) => void;
  /** Called when the user cancels — usually `close()` from the drawer host. */
  onCancel: () => void;
  /** Optional pre-fill values (e.g. when invoked from a context with hints). */
  initialValues?: EntityFormValues;
};

function defaultFor(field: EntityFormFieldDef): string | string[] | boolean {
  if (field.type === 'multiselect') return [];
  if (field.type === 'checkbox') return false;
  return '';
}

export function EntityFormDrawer({
  intro,
  fields,
  submitLabel = 'Create',
  onSubmit,
  onCancel,
  initialValues,
}: EntityFormDrawerProps) {
  const initial: EntityFormValues = useMemo(() => {
    const out: EntityFormValues = {};
    for (const f of fields) {
      out[f.key] = initialValues?.[f.key] ?? defaultFor(f);
    }
    return out;
  }, [fields, initialValues]);

  const [values, setValues] = useState<EntityFormValues>(initial);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const missingRequired = useMemo(() => {
    return fields
      .filter((f) => 'required' in f && f.required)
      .filter((f) => {
        const v = values[f.key];
        if (typeof v === 'string') return v.trim() === '';
        if (Array.isArray(v)) return v.length === 0;
        return v === undefined;
      })
      .map((f) => f.key);
  }, [fields, values]);

  function update(key: string, value: string | string[] | boolean) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitAttempted(true);
    if (missingRequired.length > 0) return;
    onSubmit(values);
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col flex-1 overflow-hidden"
      data-comment-anchor="EntityFormDrawer"
    >
      <div className="flex-1 p-5 overflow-y-auto scroll-soft">
        {intro ? (
          <div
            className="text-xs mb-4"
            style={{ color: 'var(--ink-tertiary)', lineHeight: 1.55 }}
          >
            {intro}
          </div>
        ) : null}

        {fields.map((field) => (
          <FieldRenderer
            key={field.key}
            field={field}
            value={values[field.key]}
            onChange={(v) => update(field.key, v)}
            showError={
              submitAttempted && missingRequired.includes(field.key)
            }
          />
        ))}
      </div>

      <div
        className="flex items-center justify-end gap-2 px-5 py-3"
        style={{ borderTop: '1px solid var(--hairline)' }}
      >
        {submitAttempted && missingRequired.length > 0 ? (
          <span
            className="text-xs mr-auto"
            style={{ color: 'var(--state-blocked)' }}
          >
            {missingRequired.length} required field
            {missingRequired.length === 1 ? '' : 's'} missing
          </span>
        ) : null}
        <Button variant="ghost" onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button variant="primary" type="submit">
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

function FieldRenderer({
  field,
  value,
  onChange,
  showError,
}: {
  field: EntityFormFieldDef;
  value: string | string[] | boolean | undefined;
  onChange: (next: string | string[] | boolean) => void;
  showError: boolean;
}) {
  const labelStyle: CSSProperties = {
    color: 'var(--ink-secondary)',
    fontSize: 11,
    letterSpacing: '0.02em',
    display: 'block',
    marginBottom: 4,
  };
  const inputStyle: CSSProperties = {
    width: '100%',
    background: 'var(--surface-1)',
    color: 'var(--ink-primary)',
    border: `1px solid ${showError ? 'var(--state-blocked)' : 'var(--hairline)'}`,
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: 13,
    fontFamily: 'inherit',
    outline: 'none',
  };
  const hintStyle: CSSProperties = {
    color: 'var(--ink-tertiary)',
    fontSize: 11,
    marginTop: 4,
  };

  return (
    <div className="mb-4">
      <label style={labelStyle}>
        {field.label}
        {'required' in field && field.required ? (
          <span style={{ color: 'var(--state-blocked)', marginLeft: 4 }}>*</span>
        ) : null}
      </label>

      {field.type === 'text' && (
        <input
          type="text"
          value={typeof value === 'string' ? value : ''}
          onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
          placeholder={field.placeholder}
          style={inputStyle}
        />
      )}

      {field.type === 'textarea' && (
        <textarea
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={field.rows ?? 3}
          style={{ ...inputStyle, lineHeight: 1.5, resize: 'vertical' }}
        />
      )}

      {field.type === 'date' && (
        <input
          type="date"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        />
      )}

      {field.type === 'select' && (
        <select
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          style={inputStyle}
        >
          <option value="">— Select —</option>
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      )}

      {field.type === 'multiselect' && (
        <div
          className="grid gap-1 max-h-32 overflow-y-auto scroll-soft"
          style={{
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            padding: 6,
            background: 'var(--surface-1)',
            border: '1px solid var(--hairline)',
            borderRadius: 6,
          }}
        >
          {field.options.map((opt) => {
            const checked = Array.isArray(value) && value.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex items-center gap-2"
                style={{ fontSize: 11, color: 'var(--ink-secondary)' }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const current = Array.isArray(value) ? value : [];
                    onChange(
                      checked
                        ? current.filter((v) => v !== opt.value)
                        : [...current, opt.value],
                    );
                  }}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </div>
      )}

      {field.type === 'checkbox' && (
        <label
          className="flex items-center gap-2"
          style={{ fontSize: 12, color: 'var(--ink-secondary)', cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />
          <span>{field.hint ?? 'Enable'}</span>
        </label>
      )}

      {field.type !== 'checkbox' && field.hint ? (
        <div style={hintStyle}>{field.hint}</div>
      ) : null}
    </div>
  );
}
